import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

function HowItWorks() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location]);
  return (
    <>
      <section className="page-hero">
        <h1>How It Works</h1>
        <p>Discover how AGRO-INVEST connects farmers, investors, and suppliers through AI-powered financing.</p>
      </section>

      <div className="page-content how-it-works-page">
        <div className="page-image">
          <img src="https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=800&q=80" alt="Modern farming technology" />
        </div>
        {/* Farmers */}
        <section id="farmers" className="how-section">
          <h2>For Farmers</h2>
          <p className="how-intro">Register via WhatsApp, get AI-evaluated, and receive input financing—all from your phone.</p>
          <h3>1. Start on WhatsApp</h3>
          <p>Initiate registration through our WhatsApp Farmer Bot. No app download required—just message us and follow the guided workflow.</p>
          <h3>2. Share Your Information</h3>
          <p>We collect your personal details (name, phone, location, farming experience) and farm production data (crop or livestock type, farm size, production history). You can also upload farm images for verification.</p>
          <h3>3. Capture Farm Location</h3>
          <p>Share your farm GPS coordinates so we can verify location and support geospatial risk analysis.</p>
          <h3>4. Digital Consent</h3>
          <p>Review and accept our digital consent agreement before submission. Your data is stored securely and used only for financing and platform operations.</p>
          <h3>5. AI Risk Evaluation</h3>
          <p>Our AI Risk Engine analyzes your farm data, predicts yields, estimates climate risk, and assigns a risk classification (A–E). This helps match you with appropriate financing.</p>
          <h3>6. Receive Input Financing</h3>
          <p>Once approved, you receive digital vouchers for seeds, fertilizer, livestock feed, or other inputs. Redeem them at verified suppliers via WhatsApp verification.</p>
          <p><Link to="/contact" className="btn btn-primary">Get Started as a Farmer</Link></p>
        </section>

        {/* Investors */}
        <section id="investors" className="how-section">
          <h2>For Investors</h2>
          <p className="how-intro">Browse farm opportunities, view AI risk ratings, and fund agricultural projects with transparency.</p>
          <h3>1. Explore the Marketplace</h3>
          <p>Browse our farm investment listings. Each opportunity includes farm profile, location, crop or livestock type, AI-generated risk rating (A–E), and expected return.</p>
          <h3>2. Review Risk & Returns</h3>
          <p>Our AI Risk Engine evaluates each farm using yield prediction, climate risk modeling, and default probability. You see clear risk classifications and financing recommendations before investing.</p>
          <h3>3. Select Farms to Fund</h3>
          <p>Choose the farms you want to support. Your capital goes toward input financing—seeds, fertilizer, feed—enabling farmers to grow and repay through production.</p>
          <h3>4. Track Your Investment</h3>
          <p>The system tracks all investment contributions. You receive updates via WhatsApp on farm progress, redemption activity, and platform milestones.</p>
          <h3>5. Register as an Investor</h3>
          <p>Sign up through our website to access the full marketplace, create an investor profile, and start funding agricultural projects.</p>
          <p>
            <Link to="/marketplace" className="btn btn-primary">View Marketplace</Link>
            <Link to="/contact" className="btn btn-outline" style={{ marginLeft: '0.5rem', borderColor: 'var(--bg-dark)', color: 'var(--bg-dark)' }}>Register as Investor</Link>
          </p>
        </section>

        {/* Suppliers */}
        <section id="suppliers" className="how-section">
          <h2>For Suppliers</h2>
          <p className="how-intro">Provide agricultural inputs through our voucher system and reach verified farmers.</p>
          <h3>1. Register as a Supplier</h3>
          <p>Submit your supplier registration through our platform. We verify your business and integrate you into our input financing network.</p>
          <h3>2. Administrator Approval</h3>
          <p>Platform administrators review and approve supplier accounts. Once approved, you gain access to the voucher system and farmer network.</p>
          <h3>3. Input Categories</h3>
          <p>We manage agricultural input categories including seeds, fertilizer, livestock feed, and more. Your catalog aligns with these categories for seamless voucher redemption.</p>
          <h3>4. Receive & Verify Vouchers</h3>
          <p>Approved farmers receive digital vouchers for inputs. When they visit your store, verify the voucher through WhatsApp. The system records each redemption transaction.</p>
          <h3>5. Track Financing Activity</h3>
          <p>Monitor all supplier financing activities through the platform. View redemption history, farmer engagement, and settlement data.</p>
          <h3>Benefits for Suppliers</h3>
          <ul>
            <li>Access to pre-verified, financed farmers</li>
            <li>Reduced credit risk—vouchers are pre-funded</li>
            <li>Digital verification via WhatsApp</li>
            <li>Transparent tracking and reporting</li>
          </ul>
          <p><Link to="/contact" className="btn btn-primary">Partner as a Supplier</Link></p>
        </section>
      </div>
    </>
  );
}

export default HowItWorks;
